import React, { ReactElement } from 'react';
import {
    Box,
    Button,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';

import { TStatisticsView } from './statistics-view.types';
import {
    StatisticsViewContainer,
    StyledHeader,
    StyledIncidentCount,
    StyledCategory,
    StyledHDXButtonContainer,
    StyledBottom,
} from './statistics-view.styled';
import { IncidentEffectsReport, TableReport } from '@components';
import { health, REPORT_LABELS, hdxLinks } from '@constants';

import { format } from 'date-fns';
import { formatNumber, getAlphabeticallySortedData } from '@helpers';

const isHdxCountry = (countryIsoCode: string): boolean => {
    return countryIsoCode in hdxLinks;
};

export function StatisticsView({
    filter,
    filterCategory,
    globalStatistics,
    selectedCountry,
    clearSelectedCountry,
}: TStatisticsView): ReactElement {
    // Prevent destructuring errors if required data is not available
    if (!selectedCountry && !globalStatistics) return <></>;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Define data for the active view
    const panelTitle = selectedCountry
        ? selectedCountry.country
        : 'ALL COUNTRIES';
    const totalIncidents = selectedCountry
        ? selectedCountry.totalIncidents
        : globalStatistics.totalIncidents;
    const incidentData = selectedCountry ? selectedCountry : globalStatistics;

    let perpetrators, weaponUse, healthMeasureType;
    if (selectedCountry) {
        ({ perpetrators, weaponUse, healthMeasureType } = selectedCountry);
    }

    return (
        <StatisticsViewContainer>
            <Box>
                <StyledHeader>
                    <Typography
                        align="center"
                        variant={isSmallScreen ? undefined : 'h6'}
                        style={{
                            color: filterCategory.colour,
                            lineHeight: '1.2',
                        }}
                    >
                        {panelTitle}
                    </Typography>
                    {selectedCountry && (
                        <IconButton onClick={clearSelectedCountry}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </StyledHeader>
                <Typography align="center">
                    {format(new Date(filter.fromDate), 'dd/MM/yyyy')} -{' '}
                    {format(new Date(filter.toDate), 'dd/MM/yyyy')}
                </Typography>
            </Box>

            {isSmallScreen ? (
                <Box pt={2} pb={4}>
                    <Typography
                        align="center"
                        variant="h5"
                        style={{ color: filterCategory.colour }}
                    >
                        {filterCategory?.icon && (
                            <filterCategory.icon width="19px" height="19px" />
                        )}
                        <StyledIncidentCount>
                            <strong>{formatNumber(totalIncidents)}</strong>
                        </StyledIncidentCount>
                        {filterCategory?.label.toLowerCase()} incidents
                    </Typography>
                </Box>
            ) : (
                <Box py={3}>
                    <StyledCategory style={{ color: filterCategory.colour }}>
                        {filterCategory?.icon && (
                            <Box width={32} height={32} marginRight={1}>
                                <filterCategory.icon
                                    width="100%"
                                    height="100%"
                                />
                            </Box>
                        )}
                        <Typography variant="h6">
                            {filterCategory?.label}
                        </Typography>
                    </StyledCategory>
                    <Typography
                        align="center"
                        variant="h2"
                        style={{ color: filterCategory.colour }}
                    >
                        {formatNumber(totalIncidents)}
                    </Typography>
                    <Typography align="center">incidents</Typography>
                    <Typography align="center">affected health care</Typography>
                </Box>
            )}

            <IncidentEffectsReport
                {...incidentData}
                filterCategory={filterCategory}
                isSmallScreen={isSmallScreen}
            />
            {selectedCountry && isHdxCountry(selectedCountry.isoCode) && (
                <StyledHDXButtonContainer>
                    <Tooltip
                        title="Download country data from HDX"
                        placement="top"
                        arrow
                    >
                        <Button
                            href={hdxLinks[selectedCountry.isoCode].link}
                            rel="noreferrer"
                            target="_blank"
                            variant="outlined"
                        >
                            HDX Data
                            <GetAppIcon fontSize="small" />
                        </Button>
                    </Tooltip>
                </StyledHDXButtonContainer>
            )}
            {selectedCountry && (
                <StyledBottom id="statistics-styled-bottom">
                    <div>
                        {perpetrators && (
                            <TableReport
                                header={REPORT_LABELS.PERPETRATOR_CATEGORY}
                                data={getAlphabeticallySortedData(perpetrators)}
                                colour={filterCategory.colour}
                            />
                        )}
                    </div>
                    <div>
                        {weaponUse && (
                            <TableReport
                                header={REPORT_LABELS.WEAPON_CATEGORY}
                                data={getAlphabeticallySortedData(weaponUse)}
                                colour={filterCategory.colour}
                            />
                        )}
                    </div>
                    {filter.category === health.filters.categories.outbreak && (
                        <div>
                            {healthMeasureType && (
                                <TableReport
                                    header={
                                        REPORT_LABELS.HEALTH_MEASURE_CATEGORY
                                    }
                                    data={getAlphabeticallySortedData(
                                        healthMeasureType
                                    )}
                                    colour={filterCategory.colour}
                                />
                            )}
                        </div>
                    )}
                </StyledBottom>
            )}
        </StatisticsViewContainer>
    );
}
